/**
 * Общие части экранов дашборда: метка актуальности, плитка метрики,
 * блок «как посчитано», выбор периода.
 *
 * Всё, что здесь есть, — про **финансовый** интерфейс и потому живёт в
 * сервисе, а не в каркасе платформы: метка актуальности данных нужна
 * только там, где есть цифры из внешнего источника, а плитка метрики
 * знает про «недостоверно» и «не с чем сравнить» (§9.3). Кнопки, карточки
 * и модальное окно берутся у каркаса и здесь не переписываются (§13.2 ТЗ
 * платформы).
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Card, Modal } from '@shell/ui';
import { ApiError } from '@shell/api';
import { ErrorState } from '@shell/states';
import { formatMoney, formatNumber } from '@shell/format';
import type { Explanation, Freshness, MetricView, PeriodParams } from './api';

/* ── Метка актуальности ───────────────────────────────────────────── */

/**
 * §16.3, требование без исключений: «ни один экран не показывает цифры
 * без метки актуальности».
 *
 * Метка приходит с сервера готовой и здесь **не собирается** — тот же
 * принцип, что в боте: формат, собранный в двух местах, разойдётся в
 * двух местах. Здесь только оформление.
 *
 * Остановившийся сбор выделяется цветом предупреждения, а не прячется:
 * цифры при этом показываются — они настоящие, просто вчерашние, и
 * скрыть их значило бы оставить человека вовсе без данных.
 */
export function FreshnessBadge({ freshness }: { freshness: Freshness }) {
  const classes = ['freshness'];
  if (freshness.stale) classes.push('freshness--stale');
  if (!freshness.started) classes.push('freshness--empty');
  return (
    <div className={classes.join(' ')} role="status">
      <span className="freshness__headline">{freshness.headline}</span>
      {freshness.details.length > 0 && (
        <span className="freshness__details">{freshness.details.join(' · ')}</span>
      )}
    </div>
  );
}

/* ── Значение метрики ─────────────────────────────────────────────── */

/**
 * Число метрики словами человека.
 *
 * `null` — прочерк, а не ноль. Это правило §9.1, и нарушить его в
 * интерфейсе так же плохо, как в расчёте: «средний чек 0 ₽» читается как
 * «чек бесплатный», а не как «чеков не было».
 */
export function metricValue(metric: MetricView): string {
  if (metric.value === null) return '—';
  const numeric = Number(metric.value);
  if (!Number.isFinite(numeric)) return metric.value;
  if (metric.unit === '₽') return formatMoney(numeric);
  if (metric.unit === '%') return `${formatNumber(numeric, 1)} %`;
  return `${formatNumber(numeric, numeric % 1 === 0 ? 0 : 1)} ${metric.unit}`.trim();
}

/** Сравнение в процентах: «+12 %» / «−3 %». `null` — сравнивать не с чем. */
function comparison(value: string | null): { text: string; tone: string } | null {
  if (value === null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  // Порог в один процент — не округление, а смысл: полпроцента выручки
  // за день это несколько чеков, и называть их ростом значит учить
  // человека реагировать на шум.
  if (Math.abs(numeric) < 1) return { text: 'как обычно', tone: 'flat' };
  const sign = numeric > 0 ? '+' : '−';
  return {
    text: `${sign}${formatNumber(Math.abs(numeric))} %`,
    tone: numeric > 0 ? 'up' : 'down',
  };
}

const COMPARISON_TITLES: Record<string, string> = {
  same_weekday: 'к обычному этому дню недели',
  yesterday: 'ко вчера',
  target: 'к плану',
  previous: 'к прошлому периоду',
};

/**
 * Плитка «Обзора» — §14.2.
 *
 * Три обязательных элемента и один необязательный: название, число,
 * сравнение и оговорка о недостоверности. Оговорка стоит **сразу под
 * числом**, а не в конце карточки: человек, дочитавший до конца, уже
 * принял решение по первой строке.
 */
export function MetricTile({
  metric,
  onExplain,
}: {
  metric: MetricView;
  onExplain?: (metric: MetricView) => void;
}) {
  const comparisons = Object.entries(metric.comparisons)
    .map(([key, value]) => ({ key, view: comparison(value) }))
    .filter((row) => row.view !== null)
    // §13.5 ограничивает тремя сравнениями сообщение бота; на экране
    // места больше, но три — предел читаемости, а не ширины.
    .slice(0, 3);

  return (
    <div className="tile">
      <div className="tile__name">{metric.name}</div>
      <div className="tile__value">{metricValue(metric)}</div>

      {!metric.reliable && (
        <div className="tile__caveat" role="note">
          Недостоверно
        </div>
      )}

      {comparisons.length > 0 ? (
        <ul className="tile__comparisons">
          {comparisons.map(({ key, view }) => (
            <li key={key} className={`delta delta--${view!.tone}`}>
              <strong>{view!.text}</strong> {COMPARISON_TITLES[key] ?? key}
            </li>
          ))}
        </ul>
      ) : (
        // Молчание читалось бы как «всё как обычно». Причина другая —
        // истории мало, и её надо назвать (§2 правил интерфейса).
        <p className="tile__comparisons faint">Сравнивать пока не с чем</p>
      )}

      {onExplain && (
        <Button small variant="quiet" onClick={() => onExplain(metric)}>
          Как посчитано
        </Button>
      )}
    </div>
  );
}

/* ── «Как посчитано» ──────────────────────────────────────────────── */

/**
 * §15: «пользователь, которому цифра не нравится, должен иметь
 * возможность проверить её, а не отвергнуть сервис целиком».
 *
 * Текст приходит с сервера собранным — формула, период, источник,
 * исключённое, оговорки. Здесь он показывается **дословно**: пересказ
 * объяснения своими словами в браузере означал бы третий текст той же
 * формулы, после базы и бота.
 */
export function ExplanationModal({
  metric,
  onClose,
}: {
  metric: MetricView | null;
  onClose: () => void;
}) {
  if (!metric) return null;
  return (
    <Modal title={`${metric.name}: как посчитано`} onClose={onClose}>
      <pre className="explanation">{metric.explanation.text}</pre>
      {metric.caveats.length > 0 && (
        <div className="explanation__caveats">
          {metric.caveats.map((caveat, index) => (
            <p key={index}>{caveat}</p>
          ))}
        </div>
      )}
    </Modal>
  );
}

export function useExplanation(): [
  MetricView | null,
  (metric: MetricView) => void,
  () => void,
] {
  const [shown, setShown] = useState<MetricView | null>(null);
  return [shown, setShown, () => setShown(null)];
}

/* ── Период ───────────────────────────────────────────────────────── */

/** Готовые отрезки: то, что спрашивают в девяти случаях из десяти. */
export const PRESETS: { code: string; title: string; days: number }[] = [
  { code: '7', title: 'Неделя', days: 7 },
  { code: '30', title: 'Месяц', days: 30 },
  { code: '90', title: 'Квартал', days: 90 },
];

function isoDaysAgo(days: number, from: Date): string {
  const date = new Date(from);
  date.setDate(date.getDate() - days + 1);
  return date.toISOString().slice(0, 10);
}

/** Столько дней достраивается ко второй дате, если первой ещё нет.
 *  То же число, что `DEFAULT_DAYS` в `api/periods.py`: период, который
 *  дашборд показывает без спроса, должен быть один и тот же с обеих
 *  сторон. */
const DEFAULT_DAYS = 7;

/**
 * Выбор периода. Состояние живёт в адресе строки браузера (§14.4:
 * «состояние фильтров в URL, чтобы бот мог давать точную ссылку»), и
 * потому этот компонент ничего не хранит сам — он только сообщает
 * выбор наверх.
 *
 * Кнопки считают отрезок от **последнего дня данных**, а не от
 * сегодняшнего: «месяц» на данных, доехавших до пятницы, должен
 * заканчиваться пятницей, иначе последние дни графика — пустые, и
 * выглядит это как обвал выручки.
 */
export function PeriodPicker({
  period,
  anchor,
  onChange,
}: {
  period: PeriodParams;
  anchor: string | null;
  onChange: (next: PeriodParams) => void;
}) {
  const end = anchor ?? new Date().toISOString().slice(0, 10);

  /**
   * Даты уезжают наверх **только парой**.
   *
   * Сервер не считает период, заданный наполовину (`api/periods.py`:
   * «одна дата без второй — почти всегда обрезанная ссылка»), и это
   * верно для ссылки из бота. Но человек ставит даты по одной, и первая
   * же поставленная дата была ровно этим случаем: экран подменялся
   * ошибкой вместе с самими полями дат, и починить введённое было
   * нечем. Поэтому недостающая половина достраивается здесь — от
   * последнего дня данных или на неделю назад.
   *
   * Пустое поле не меняет ничего: очищенная дата — это не период, а
   * середина набора.
   */
  const changeFrom = (value: string) => {
    if (!value) return;
    onChange({ ...period, from: value, to: period.to ?? end });
  };

  const changeTo = (value: string) => {
    if (!value) return;
    onChange({
      ...period,
      to: value,
      from: period.from ?? isoDaysAgo(DEFAULT_DAYS, new Date(value)),
    });
  };

  return (
    <div className="period">
      <div className="period__presets">
        {PRESETS.map((preset) => (
          <Button
            key={preset.code}
            small
            variant="quiet"
            onClick={() =>
              onChange({
                ...period,
                from: isoDaysAgo(preset.days, new Date(end)),
                to: end,
              })
            }
          >
            {preset.title}
          </Button>
        ))}
      </div>
      <div className="period__dates">
        <label>
          <span className="faint">с</span>
          <input
            type="date"
            value={period.from ?? ''}
            max={period.to ?? undefined}
            onChange={(event) => changeFrom(event.target.value)}
          />
        </label>
        <label>
          <span className="faint">по</span>
          <input
            type="date"
            value={period.to ?? ''}
            min={period.from ?? undefined}
            onChange={(event) => changeTo(event.target.value)}
          />
        </label>
      </div>
    </div>
  );
}

/**
 * Ошибка на экране, у которого есть период.
 *
 * Отдельный компонент, а не `ErrorState` напрямую, ради двух правил
 * `docs/interface-principles.md`: §6 — ошибка называет причину и
 * следующий шаг, §8 — экран не оставляет человека без кнопки.
 *
 * Поэтому выбор периода **остаётся на месте**: чаще всего чинить надо
 * именно его. Раньше экран подменялся целиком, поля дат исчезали вместе
 * с ним, и у человека не оставалось ни одного действия, кроме как
 * закрыть приложение, — так это и выглядело: «Mini App вылетает после
 * даты».
 *
 * Текст берётся у сервера, когда он его дал: «Период задаётся двумя
 * датами сразу» полезнее, чем «Похоже, сервер сейчас недоступен».
 */
export function ScreenError({
  error,
  period,
  anchor,
  onPeriodChange,
  onRetry,
}: {
  error: unknown;
  period: PeriodParams;
  anchor: string | null;
  onPeriodChange: (next: PeriodParams) => void;
  onRetry: () => void;
}) {
  const detail = error instanceof ApiError ? error.detail : null;
  return (
    <div className="screen">
      <PeriodPicker period={period} anchor={anchor} onChange={onPeriodChange} />
      <ErrorState
        title={detail ? 'Этот период показать не получилось' : undefined}
        text={detail ?? undefined}
        onRetry={onRetry}
      />
    </div>
  );
}

/* ── Мелочи ───────────────────────────────────────────────────────── */

export function Section({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card title={title} actions={actions}>
      {children}
    </Card>
  );
}

/**
 * Ссылка на выгрузку. Обычная `<a download>`, а не кнопка с `fetch`:
 * файл забирает браузер сам, и у человека появляется файл в
 * «Загрузках», а не строка в памяти вкладки (§14.4).
 */
export function DownloadLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className="button button--quiet button--small export-action" href={href} download>
      {children}
    </a>
  );
}

export function explanationText(explanation: Explanation): string {
  return explanation.text;
}
